import { type Dispatch } from 'react';

import type { DraftEntry, WeightUnit } from './state/loggerDraft';
import type { LoggerAction } from './state/loggerDraftTypes';

// SetCountRow ＋/− handlers, extracted from useTimelineLogger to keep that hook
// under the 500-line cap (LG-14 split). Behaviour-preserving — the dispatch
// logic is unchanged: in a superset/circuit ＋/− fans out to EVERY member so the
// group stays symmetric (Antenore 2026-06-01), a standalone ＋ is an explicit
// off-plan `blank` set (drops at save if untouched, weight still carries), and
// each handler clears the manual selection so the new binding follows.
export function useSetCountHandlers(args: {
  activeEntryIdx: number;
  activeEntry: DraftEntry | null;
  defaultUnit: WeightUnit;
  /** Trailing set index of the active entry (= the group's last round). */
  lastSetIdx: number;
  canRemove: boolean;
  dispatch: Dispatch<LoggerAction>;
  setSelectedSetIdx: (idx: number | null) => void;
}) {
  const {
    activeEntryIdx,
    activeEntry,
    defaultUnit,
    lastSetIdx,
    canRemove,
    dispatch,
    setSelectedSetIdx,
  } = args;

  const onAddSet = () => {
    if (activeEntryIdx < 0) return;
    if (activeEntry?.group) {
      dispatch({
        type: 'add_round_to_group',
        groupId: activeEntry.group.id,
        defaultUnit,
      });
    } else {
      dispatch({ type: 'add_set', entryIdx: activeEntryIdx, defaultUnit, blank: true });
    }
    setSelectedSetIdx(null);
  };

  const onRemoveSet = () => {
    if (activeEntryIdx < 0 || !canRemove) return;
    if (activeEntry?.group) {
      dispatch({
        type: 'remove_round_from_group',
        groupId: activeEntry.group.id,
        roundIdx: lastSetIdx,
      });
    } else {
      dispatch({ type: 'remove_set', entryIdx: activeEntryIdx, setIdx: lastSetIdx });
    }
    setSelectedSetIdx(null);
  };

  return { onAddSet, onRemoveSet };
}
